
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { 
  Settings, 
  Users, 
  Activity, 
  LogOut
} from "lucide-react";

interface SettingsTabProps {
  userEmail: string;
  onSignOut: () => void;
}

export const SettingsTab = ({ userEmail, onSignOut }: SettingsTabProps) => {
  const { language } = useLanguage();
  const { toast } = useToast();

  const handleEditProfile = () => {
    toast({
      title: language === 'ar' ? 'قريباً' : 'Coming Soon',
      description: language === 'ar' ? 'هذه الميزة ستكون متاحة قريباً' : 'This feature will be available soon',
    });
  };

  const handleManageNotifications = () => {
    toast({
      title: language === 'ar' ? 'قريباً' : 'Coming Soon',
      description: language === 'ar' ? 'إدارة الإشعارات ستكون متاحة قريباً' : 'Notification management will be available soon',
    });
  };

  return (
    <div className="grid gap-6">
      <Card className="bg-card/80 backdrop-blur-sm border shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
              <Settings className="h-6 w-6 text-primary-foreground" />
            </div>
            {language === 'ar' ? 'إعدادات الحساب' : 'Account Settings'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-card/50 to-card border hover:shadow-md transition-all duration-300">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-foreground">
                  {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                </p>
                <p className="text-sm text-muted-foreground">{userEmail}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleEditProfile} className="shadow-sm hover:shadow-md transition-all duration-200">
              {language === 'ar' ? 'تعديل' : 'Edit'}
            </Button>
          </div>
          
          <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-card/50 to-card border hover:shadow-md transition-all duration-300">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-md">
                <Activity className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-foreground">
                  {language === 'ar' ? 'إشعارات البريد الإلكتروني' : 'Email Notifications'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {language === 'ar' ? 'تلقي إشعارات حول الحملات والأداء' : 'Receive campaign updates and performance alerts'}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleManageNotifications} className="shadow-sm hover:shadow-md transition-all duration-200">
              {language === 'ar' ? 'إدارة' : 'Manage'}
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-destructive/10 to-destructive/5 border border-destructive/20 hover:shadow-md transition-all duration-300">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-destructive to-destructive/80 flex items-center justify-center shadow-md">
                <LogOut className="h-6 w-6 text-destructive-foreground" />
              </div>
              <div>
                <p className="font-semibold text-destructive">
                  {language === 'ar' ? 'تسجيل الخروج' : 'Sign Out'}
                </p>
                <p className="text-sm text-destructive/80">
                  {language === 'ar' ? 'تسجيل الخروج من حسابك بشكل آمن' : 'Securely sign out of your account'}
                </p>
              </div>
            </div>
            <Button variant="destructive" size="sm" onClick={onSignOut} className="shadow-sm hover:shadow-md transition-all duration-200">
              {language === 'ar' ? 'تسجيل الخروج' : 'Sign Out'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
